import {observer} from 'mobx-react';
import React, {useEffect, useCallback, useRef, useState} from 'react';
import {PriorityIcon, Flex, AIStarIcon, Title} from '@priority-software/priority-style-react';
import {sendMonitor} from '../../utils/monitoring';
import {HoveringZoomButton} from '../../_legacy/FieldSearch/components/HoveringZoomButton';
import {formsStore} from '../../_legacy/Form/stores/FormsStore';
import {featurePopoverRef} from '../FeaturePopover/FeaturePopover.root';
import {featuresIDs} from '../FeaturePopover/constants/map';
import {PopoverContent} from '../FeaturePopover/FeaturePopover';
import {keyCodes} from '../../constants';
import texts from './texts';
import {isMacOS} from '../../utils/platform';
import {getZoomFname, getInfoMap} from './utils/utils';
import {
    getMoreInfoScreenSuggestion,
    IndexedForm
} from '../../services/indexDB/indexeddbServiceZoomedScreens';
import {RecommendationDialog} from './components/RecommendationDialog';
import {showDialog} from '../Personalization/actions';
import {isAutomation} from '../FeaturePopover/actions/feature';

type Props = {
    fieldName: string;
    borderStyle: {borderRight?: string; borderLeft?: string};
};

export type MoreInfoSuggestion = {
    zoomFname: string;
    indexedForm: IndexedForm;
};

export const MoreInfoTrigger = observer(({fieldName, borderStyle}: Props) => {
    const [suggestion, setSuggestion] = useState<MoreInfoSuggestion>(null);
    const [open, setOpen] = useState<boolean>(false);
    const moreInfoTriggerRef = useRef();
    const {currentForm} = formsStore;

    const onApprove = useCallback(async () => {
        open && setOpen(false);
        const infoMap = await getInfoMap();
        const supportedEntities = infoMap[suggestion.zoomFname];
        supportedEntities.unshift(suggestion.indexedForm.form);
        setSuggestion(null);
        currentForm.setCurrentFieldMoreInfo(supportedEntities);
        currentForm.openFieldMoreInfo();
    }, [suggestion, open, currentForm]);

    const closeRecommendationDialog = () => {
        setOpen(false);
        setSuggestion(null);
    };

    const onOpenFieldMoreInfo = useCallback(async () => {
        if (suggestion) {
            sendMonitor('PersonalizationMoreDetailsIcon');
            await onApprove();
        } else {
            currentForm.openFieldMoreInfo();
        }
    }, [currentForm, suggestion, onApprove]);

    const handleIconClick = async () => {
        const isOpen = currentForm?.showMoreInfo;

        if (!isOpen && (window as any).openHTTPs === 0) {
            sendMonitor('MoreDetailsIcon', null, fieldName);
            await onOpenFieldMoreInfo();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [suggestion, open, currentForm]);

    const handleKeyDown = useCallback(
        async e => {
            const isMac = isMacOS();
            switch (e.keyCode) {
                case keyCodes.MoreInfo:
                    if ((isMac && e.metaKey) || e.ctrlKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        sendMonitor('MoreDetailsKey', null, fieldName);
                        await onOpenFieldMoreInfo();
                    }
                    break;
                default:
                    featurePopoverRef.current.onClose(true);
            }
        },
        [suggestion, open, currentForm]
    );

    const handleConfirm = () => {
        currentForm?.openFieldMoreInfo();
        sendMonitor('MoreDetailsPromotionYes');
    };

    const handleIgnore = () => {
        sendMonitor('MoreDetailsPromotionNo');
    };

    const showFeaturePopover =
        !isAutomation() && currentForm?.isShowFeaturePopover?.(featuresIDs.moreInfo);

    const popoverContent: PopoverContent = {
        title: (
            <Flex justifyContent="flex-start" style={{gap: '8px'}}>
                <AIStarIcon />
                <Title skin={'h5'} text={texts.feature_popover_title} />
            </Flex>
        ),
        content: texts.feature_popover_content,
        secondaryButtonText: texts.feature_popover_ignore_btn,
        primaryButtonText: texts.feature_popover_approve_btn
    };

    useEffect(() => {
        const currentForm = formsStore.currentForm;
        if (currentForm && !currentForm.isRecon) {
            const currentColumn = currentForm.getColumnByName(fieldName);
            const zoomFname = getZoomFname(currentForm, currentColumn);
            zoomFname &&
                getMoreInfoScreenSuggestion(zoomFname).then(moreInfoSuggestion => {
                    if (moreInfoSuggestion && showDialog()) {
                        setSuggestion({zoomFname, indexedForm: moreInfoSuggestion});
                        setOpen(true);
                    } else if (showFeaturePopover && moreInfoTriggerRef.current) {
                        featurePopoverRef?.current?.showFeaturePopover(
                            featuresIDs.moreInfo,
                            moreInfoTriggerRef.current,
                            handleIgnore,
                            handleConfirm,
                            popoverContent
                        );
                    }
                });
        }
    }, []);

    return (
        <>
            <HoveringZoomButton
                onZoom={handleIconClick}
                icon={PriorityIcon.moreInfo}
                tooltip={texts.info_icon_tooltip}
                elementRef={moreInfoTriggerRef}
                borderStyle={borderStyle}
            />
            {suggestion && (
                <RecommendationDialog
                    open={open}
                    suggestion={suggestion}
                    anchor={moreInfoTriggerRef.current}
                    onApprove={onApprove}
                    closeDialog={closeRecommendationDialog}
                />
            )}
        </>
    );
});
